import { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { MessageSquare, ChevronRight, Sparkles, Loader2, CheckCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { PageLoading, Loading } from '@/components/shared/Loading'
import { Pagination } from '@/components/shared/Pagination'
import { formatDateTime, formatRelativeTime } from '@/utils/formatDate'
import { useAllThreads, useRequestThreadAnalysis } from '@/hooks/useThreads'

export function ThreadsPage() {
  const [page, setPage] = useState(1)
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined)
  const [hasAnalysisFilter, setHasAnalysisFilter] = useState<boolean | undefined>(undefined)
  const [userIdSearch, setUserIdSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  const { threads, pagination, loading, error, refetch } = useAllThreads(
    page,
    20,
    isActiveFilter,
    hasAnalysisFilter,
    userIdSearch || undefined
  )

  const { requestAnalysis, isAnalyzing, getStatus, getError } = useRequestThreadAnalysis()

  const handleActiveFilterChange = (value: string) => {
    if (value === 'all') {
      setIsActiveFilter(undefined)
    } else if (value === 'active') {
      setIsActiveFilter(true)
    } else {
      setIsActiveFilter(false)
    }
    setPage(1)
  }

  const handleAnalysisFilterChange = (value: string) => {
    if (value === 'all') {
      setHasAnalysisFilter(undefined)
    } else if (value === 'analyzed') {
      setHasAnalysisFilter(true)
    } else {
      setHasAnalysisFilter(false)
    }
    setPage(1)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setUserIdSearch(searchInput)
    setPage(1)
  }

  const handleAnalyzeClick = useCallback(
    (e: React.MouseEvent, threadId: number) => {
      e.preventDefault()
      e.stopPropagation()
      // onComplete 콜백을 통해 분석 완료 시 refetch
      requestAnalysis(threadId, false, refetch).catch(() => {
        // 에러는 이미 hook에서 처리됨
      })
    },
    [requestAnalysis, refetch]
  )

  if (loading && threads.length === 0) {
    return <PageLoading />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">스레드 목록</h1>
        <span className="text-sm text-muted-foreground">
          총 {pagination?.total ?? 0}개의 스레드
        </span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">필터</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <Input
                placeholder="사용자 ID로 검색..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-48"
              />
              <Button type="submit" variant="outline" size="sm">
                검색
              </Button>
              {userIdSearch && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchInput('')
                    setUserIdSearch('')
                    setPage(1)
                  }}
                >
                  초기화
                </Button>
              )}
            </form>

            <Select
              value={isActiveFilter === undefined ? 'all' : isActiveFilter ? 'active' : 'inactive'}
              onChange={(e) => handleActiveFilterChange(e.target.value)}
            >
              <option value="all">전체 상태</option>
              <option value="active">활성 스레드</option>
              <option value="inactive">종료된 스레드</option>
            </Select>

            <Select
              value={hasAnalysisFilter === undefined ? 'all' : hasAnalysisFilter ? 'analyzed' : 'not_analyzed'}
              onChange={(e) => handleAnalysisFilterChange(e.target.value)}
            >
              <option value="all">전체 분석</option>
              <option value="analyzed">분석 완료</option>
              <option value="not_analyzed">미분석</option>
            </Select>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-destructive">
              스레드를 불러오는데 실패했습니다: {error}
            </p>
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={refetch}>
                다시 시도
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : threads.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">
              조건에 맞는 스레드가 없습니다
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {loading && <Loading size="sm" className="py-2" />}
            <div className="divide-y">
              {threads.map((thread) => (
                <div
                  key={thread.id}
                  className="flex items-center justify-between p-4 transition-colors hover:bg-muted/50"
                >
                  <Link
                    to={`/users/${thread.user_id}/chats?thread=${thread.id}`}
                    className="flex flex-1 items-center gap-3"
                  >
                    <MessageSquare className="h-5 w-5 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">스레드 #{thread.id}</span>
                        <Badge variant="outline" className="text-xs">
                          {thread.user_id}
                        </Badge>
                        {thread.is_active ? (
                          <Badge variant="success">활성</Badge>
                        ) : (
                          <Badge variant="secondary">종료됨</Badge>
                        )}
                        {/* 분석 상태 배지 */}
                        {isAnalyzing(thread.id) || thread.analysis_status === 'processing' || thread.analysis_status === 'received' ? (
                          <Badge variant="warning" className="gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            분석 중
                          </Badge>
                        ) : thread.has_analysis ? (
                          <Badge variant="default" className="gap-1">
                            <CheckCircle className="h-3 w-3" />
                            분석됨
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(thread.created_at)}
                        {thread.ended_at && ` - ${formatRelativeTime(thread.ended_at)}`}
                        {thread.duration_minutes && ` (${Math.round(thread.duration_minutes)}분)`}
                      </p>
                      {thread.first_message_preview && (
                        <p className="mt-1 max-w-lg truncate text-sm text-muted-foreground">
                          {thread.first_message_preview}
                        </p>
                      )}
                    </div>
                  </Link>
                  <div className="flex items-center gap-3">
                    <span className="whitespace-nowrap text-sm text-muted-foreground">
                      {thread.message_count} 메시지
                    </span>
                    <Button
                      variant={getError(thread.id) ? 'destructive' : thread.has_analysis ? 'outline' : 'default'}
                      size="sm"
                      onClick={(e) => handleAnalyzeClick(e, thread.id)}
                      disabled={isAnalyzing(thread.id)}
                      className="gap-1"
                      title={getError(thread.id) || undefined}
                    >
                      {isAnalyzing(thread.id) ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {getStatus(thread.id) === 'processing' ? 'AI 분석 중...' : '요청 중...'}
                        </>
                      ) : getError(thread.id) ? (
                        <>
                          <Sparkles className="h-4 w-4" />
                          재시도
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4" />
                          {thread.has_analysis ? 'AI 재분석' : 'AI 분석'}
                        </>
                      )}
                    </Button>
                    <Link to={`/users/${thread.user_id}/chats?thread=${thread.id}`}>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {pagination && pagination.total_pages > 1 && (
        <Pagination
          current={pagination.page}
          total={pagination.total_pages}
          onChange={setPage}
        />
      )}
    </div>
  )
}
